import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Slider, Snackbar, Stack, TextField, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ScienceIcon from '@mui/icons-material/Science';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import PracticalAssessment from '../components/PracticalAssessment';
import PracticalInstructions from '../components/PracticalInstructions';
import LabBench, { type BenchComponent } from './LabBench';
import GraphPlot, { type GraphMode } from './GraphPlot';
import Notebook, { type NotebookReading } from './Notebook';
import StepGuide from './StepGuide';
import { solveOhmsLawCircuit, type OhmsLawCircuitResult } from './physics/ohmsLaw';
import { validateCircuit, type WireConnection } from './physics/circuitSolver';
import './ohmsLawLab.css';

const INITIAL_COMPONENTS: BenchComponent[] = [
  { id: 'supply', type: 'battery', x: 70, y: 95, voltage: 2 },
  { id: 'rheostat', type: 'rheostat', x: 300, y: 75, resistance: 5 },
  { id: 'ammeter', type: 'ammeter', x: 565, y: 72 },
  { id: 'resistor', type: 'resistor', x: 830, y: 105, resistance: 10 },
  { id: 'switch', type: 'switch', x: 430, y: 360, closed: false },
  { id: 'voltmeter', type: 'voltmeter', x: 850, y: 360 },
];

const DEFAULT_WIRES: WireConnection[] = [
  { id: 'lead-1', from: { componentId: 'supply', terminal: 'positive' }, to: { componentId: 'rheostat', terminal: 'input' } },
  { id: 'lead-2', from: { componentId: 'rheostat', terminal: 'output' }, to: { componentId: 'ammeter', terminal: 'input' } },
  { id: 'lead-3', from: { componentId: 'ammeter', terminal: 'output' }, to: { componentId: 'resistor', terminal: 'left' } },
  { id: 'lead-4', from: { componentId: 'resistor', terminal: 'right' }, to: { componentId: 'switch', terminal: 'input' } },
  { id: 'lead-5', from: { componentId: 'switch', terminal: 'output' }, to: { componentId: 'supply', terminal: 'negative' } },
  { id: 'lead-6', from: { componentId: 'voltmeter', terminal: 'high' }, to: { componentId: 'resistor', terminal: 'left' } },
  { id: 'lead-7', from: { componentId: 'voltmeter', terminal: 'low' }, to: { componentId: 'resistor', terminal: 'right' } },
];

interface SavedSession {
  _id: string;
  title: string;
  mode: string;
  circuit: { supplyVoltage: number; fixedResistance: number; rheostatResistance: number; wireLengthMetres: number; switchClosed: boolean; components: BenchComponent[]; wires: WireConnection[] };
  readings: NotebookReading[];
  observations: string;
  conclusion: string;
  status: string;
  createdAt?: string;
  updatedAt?: string;
}

const makeDefaultSessionState = () => ({
  mode: 'verify' as 'verify' | 'length' | 'series-parallel' | 'sandbox',
  supplyVoltage: 2,
  fixedResistance: 10,
  rheostatResistance: 5,
  wireLengthMetres: 1,
  components: INITIAL_COMPONENTS.map((component) => ({ ...component })),
  wires: DEFAULT_WIRES.map((wire) => ({ ...wire })),
  readings: [] as NotebookReading[],
  observations: '',
  conclusion: '',
});

const OhmsLawLab: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { openLogin } = useAuthModal();
  const [state, setState] = useState(makeDefaultSessionState);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<SavedSession[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem('ohmsLabDark') === 'true');
  const [graphMode, setGraphMode] = useState<GraphMode>('vi');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [reportDialog, setReportDialog] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { document.documentElement.dataset.ohmsLabTheme = dark ? 'dark' : 'light'; localStorage.setItem('ohmsLabDark', String(dark)); }, [dark]);
  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  const componentPatch = useCallback((id: string, patch: Partial<BenchComponent>) => {
    setState((current) => ({ ...current, components: current.components.map((component) => component.id === id ? { ...component, ...patch } : component) }));
  }, []);

  const circuitResult: OhmsLawCircuitResult | null = useMemo(() => {
    const switchClosed = state.components.find((component) => component.id === 'switch')?.closed === true;
    const validation = validateCircuit(state.components, state.wires, { supplyVoltage: state.supplyVoltage, ammeterRange: 2 });
    if (!switchClosed || !validation.energized || !validation.valid) return null;
    const testResistance = state.mode === 'length'
      ? state.fixedResistance * state.wireLengthMetres
      : state.fixedResistance;
    try {
      return solveOhmsLawCircuit({
        supplyVoltage: state.supplyVoltage,
        resistance: testResistance,
        seriesResistance: state.rheostatResistance,
        ammeterRange: 2,
        voltmeterRange: 12,
        voltageNoiseAmplitude: 0.02,
        currentNoiseAmplitude: 0.002,
        voltageResolution: 0.01,
        currentResolution: 0.001,
      });
    } catch {
      return null;
    }
  }, [state.components, state.wires, state.supplyVoltage, state.rheostatResistance, state.fixedResistance, state.wireLengthMetres, state.mode]);

  const loadSessions = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoadingSessions(true);
    try {
      const response = await api.get('/ohms-law-sessions');
      const saved: SavedSession[] = response.data?.data || [];
      setSessions(saved);
      if (!sessionId && saved.length) {
        setSessionId(saved[0]._id);
        setState((current) => ({ ...current, ...saved[0], mode: (saved[0].mode as any) || current.mode, components: saved[0].circuit?.components?.length ? saved[0].circuit.components : current.components, wires: saved[0].circuit?.wires?.length ? saved[0].circuit.wires : current.wires, supplyVoltage: saved[0].circuit?.supplyVoltage ?? current.supplyVoltage, fixedResistance: saved[0].circuit?.fixedResistance ?? current.fixedResistance, rheostatResistance: saved[0].circuit?.rheostatResistance ?? current.rheostatResistance, wireLengthMetres: saved[0].circuit?.wireLengthMetres ?? current.wireLengthMetres, readings: saved[0].readings || [], observations: saved[0].observations || '', conclusion: saved[0].conclusion || '' }));
      }
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Could not load saved sessions. The lab still works locally.');
    } finally { setLoadingSessions(false); }
  }, [isAuthenticated, sessionId]);

  useEffect(() => { if (!authLoading && isAuthenticated) loadSessions(); }, [authLoading, isAuthenticated, loadSessions]);

  const persistSession = useCallback(async (nextState = state, knownId = sessionId) => {
    if (!isAuthenticated || !user) return;
    setSaving(true);
    try {
      let activeId = knownId;
      if (!activeId) {
        const created = await api.post('/ohms-law-sessions', { title: "Ohm's Law Verification", mode: nextState.mode });
        activeId = created.data?.data?._id;
        if (!activeId) throw new Error('Session id missing from response');
        setSessionId(activeId);
      }
      const payload = {
        mode: nextState.mode,
        circuit: { supplyVoltage: nextState.supplyVoltage, fixedResistance: nextState.fixedResistance, rheostatResistance: nextState.rheostatResistance, wireLengthMetres: nextState.wireLengthMetres, switchClosed: nextState.components.find((component) => component.id === 'switch')?.closed ?? false, components: nextState.components, wires: nextState.wires },
        readings: nextState.readings,
        observations: nextState.observations,
        conclusion: nextState.conclusion,
      };
      await api.patch(`/ohms-law-sessions/${activeId}`, payload);
      setMessage('Session saved to your account.');
      await loadSessions();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Autosave is unavailable. Your current work remains in this browser.');
    } finally { setSaving(false); }
  }, [isAuthenticated, user, state, sessionId, loadSessions]);

  useEffect(() => {
    if (!sessionId || !isAuthenticated) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { void persistSession(state, sessionId); }, 900);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [state, sessionId, isAuthenticated, persistSession]);

  const updateComponents = (components: BenchComponent[]) => {
    const switchComponent = components.find((component) => component.id === 'switch');
    setState((current) => ({ ...current, components }));
    if (switchComponent?.closed !== undefined) componentPatch('switch', { closed: switchComponent.closed });
  };

  const setVoltage = (_event: Event, value: number | number[]) => {
    const voltage = Array.isArray(value) ? value[0] : value;
    setState((current) => ({ ...current, supplyVoltage: voltage }));
    componentPatch('supply', { voltage });
  };
  const setRheostat = (_event: Event, value: number | number[]) => {
    const resistance = Array.isArray(value) ? value[0] : value;
    setState((current) => ({ ...current, rheostatResistance: resistance }));
    componentPatch('rheostat', { resistance });
  };
  const setFixedResistance = (_event: Event, value: number | number[]) => {
    const resistance = Array.isArray(value) ? value[0] : value;
    setState((current) => ({ ...current, fixedResistance: resistance }));
    componentPatch('resistor', { resistance });
  };

  const autoRecord = () => {
    if (!circuitResult?.ammeter.reading && circuitResult?.ammeter.reading !== 0) return null;
    if (circuitResult.voltmeter.reading === null) return null;
    const current = circuitResult.ammeter.reading;
    const voltage = circuitResult.voltmeter.reading;
    const lengthMetres = state.mode === 'length' ? state.wireLengthMetres : undefined;
    return { voltage, current, resistance: current === 0 ? 0 : voltage / current, lengthMetres };
  };

  const handleReset = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setState(makeDefaultSessionState());
    setSessionId(null);
    setConfirmReset(false);
    setMessage('Bench reset. Your previous saved sessions remain available.');
  };

  const resumeSession = (saved: SavedSession) => {
    setSessionId(saved._id);
    setState((current) => ({ ...current, mode: (saved.mode as any) || current.mode, components: saved.circuit?.components?.length ? saved.circuit.components : current.components, wires: saved.circuit?.wires?.length ? saved.circuit.wires : current.wires, supplyVoltage: saved.circuit?.supplyVoltage ?? current.supplyVoltage, fixedResistance: saved.circuit?.fixedResistance ?? current.fixedResistance, rheostatResistance: saved.circuit?.rheostatResistance ?? current.rheostatResistance, wireLengthMetres: saved.circuit?.wireLengthMetres ?? current.wireLengthMetres, readings: saved.readings || [], observations: saved.observations || '', conclusion: saved.conclusion || '' }));
  };

  const downloadReport = async () => {
    if (!sessionId) await persistSession(state, null);
    setPdfBusy(true);
    try {
      const { default: jsPDF } = await import('jspdf');
      const pdf = new jsPDF();
      const margin = 14;
      let y = 18;
      const addLine = (text: string, size = 10) => { pdf.setFontSize(size); const wrapped = pdf.splitTextToSize(text, 180); if (y + wrapped.length * 5 > 280) { pdf.addPage(); y = 18; } pdf.text(wrapped, margin, y); y += wrapped.length * 5 + 2; };
      addLine("Ohm's Law Virtual Laboratory Report", 18);
      addLine(`Student: ${user?.name || user?.email || 'Registered user'}`);
      addLine(`Date: ${new Date().toLocaleString()}`);
      addLine(`Mode: ${state.mode}`);
      addLine(`Circuit setting: V = ${state.supplyVoltage.toFixed(2)} V; fixed R = ${state.fixedResistance.toFixed(2)} Ω; rheostat = ${state.rheostatResistance.toFixed(2)} Ω`);
      addLine('Readings', 14);
      state.readings.forEach((row, index) => addLine(`${index + 1}. V ${row.voltage.toFixed(3)} V · I ${row.current.toFixed(4)} A · R ${row.resistance.toFixed(3)} Ω${row.lengthMetres ? ` · L ${row.lengthMetres.toFixed(3)} m` : ''} · ${row.observation || '—'}`));
      addLine('Calculated result', 14);
      if (state.readings.length >= 2) {
        const fit = (await import('./physics/ohmsLaw')).fitStraightLine(state.readings.map((row) => ({ x: row.current, y: row.voltage })));
        addLine(`Best-fit V-I slope (resistance): ${fit.slope.toFixed(4)} Ω; intercept: ${fit.intercept.toFixed(4)} V; R²: ${fit.rSquared.toFixed(4)}.`);
      } else addLine('Record at least two measurements for a best-fit resistance estimate.');
      addLine('Conclusion', 14); addLine(state.conclusion || state.observations || 'No written conclusion yet.');
      pdf.save('ohms-law-lab-report.pdf');
      if (sessionId) await api.patch(`/ohms-law-sessions/${sessionId}`, { status: 'completed', reportGeneratedAt: new Date().toISOString() });
    } catch (reportError: any) { setError(reportError?.message || 'Could not generate report.'); }
    finally { setPdfBusy(false); }
  };

  if (authLoading) return <Container sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Container>;
  if (!isAuthenticated) return <Container maxWidth="sm" sx={{ py: 8 }}><Alert severity="info" action={<Button color="inherit" onClick={() => openLogin()}>Sign in</Button>}>Sign in to access the Ohm’s Law virtual laboratory and save sessions.</Alert></Container>;

  return (
    <Box className={`ohm-lab ${dark ? 'ohm-lab-dark' : ''}`}>
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center"><Tooltip title="Back to labs"><IconButton onClick={() => navigate('/labs')}><ArrowBackIcon /></IconButton></Tooltip><Box><Typography variant="h4" fontWeight={900}>Ohm’s Law Virtual Lab</Typography><Typography color="text.secondary">Build the circuit, take real instrument readings, and record your evidence.</Typography></Box></Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button startIcon={<RestartAltIcon />} color="warning" onClick={() => setConfirmReset(true)}>Reset experiment</Button>
            <Tooltip title={dark ? 'Switch to light theme' : 'Switch to dark theme'}><IconButton onClick={() => setDark((value) => !value)} aria-label="Toggle lab theme"><Brightness4Icon /></IconButton></Tooltip>
            <Chip icon={saving ? <CircularProgress size={14} /> : <ScienceIcon />} label={saving ? 'Saving…' : sessionId ? 'Autosaved' : 'New session'} color={sessionId ? 'success' : 'default'} />
          </Stack>
        </Stack>
        {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        <PracticalAssessment practicalType="ohms-law" title="Experiment Assessment Question" questionTag={`ohms-law-${state.mode}`} />
        <PracticalInstructions
          title="Ohm’s Law practical instructions"
          introduction="Investigate the relationship between potential difference and current in a conductor. Collect repeatable readings, calculate resistance, and compare your results with the graph."
          sections={[
            { title: 'A. Verify Ohm’s Law', steps: [
              'Check that the DC supply is off and the switch is open. Identify the supply, test resistor, rheostat, ammeter, and voltmeter on the bench.',
              'Connect the supply, rheostat, ammeter, test resistor, and switch in one series loop. Connect the voltmeter in parallel across the test resistor only.',
              'Run Circuit Check. Correct any open lead, short circuit, ammeter bypass, or voltmeter that is not across the resistor before closing the switch.',
              'Set a low supply voltage and suitable rheostat resistance. Close the switch briefly and read current in amperes and potential difference in volts.',
              'Use Auto-record in the notebook. Open the switch before changing a connection or resistance setting.',
              'Repeat for at least five increasing voltage settings while keeping the resistor and its temperature as constant as possible. Record each V and I pair.',
              'Calculate R = V/I for each reading. Plot V against I; for an ohmic conductor the graph is approximately straight, and its gradient is the resistance.',
              'State whether V is proportional to I, quote the measured resistance, and comment on scatter or heating.'
            ] },
            { title: 'B. Resistance versus wire length', steps: [
              'Select Resistance vs length mode and choose a wire length with the length control.',
              'Keep the supply setting and wire material/thickness fixed. Record voltage and current for that length.',
              'Open the switch, change the length, and repeat for several lengths. Compare R = V/I or the V-I graph gradient for each length.',
              'Plot resistance against length and describe the trend. Note that real wires can warm up and change resistance.'
            ] },
            { title: 'C. Series and parallel circuits', steps: [
              'Select Series & parallel mode. Identify components in the single series path and components sharing parallel branches.',
              'Check the circuit before energising. Record supply voltage, loop or branch currents, and the voltage across each load.',
              'For series loads, compare total resistance with the sum of individual resistances. For parallel loads, compare branch currents and equivalent resistance.',
              'Use the readings to explain how adding loads changes total resistance and current.'
            ] },
            { title: 'D. Free play and reporting', steps: [
              'In Free play, change one setting at a time and use Circuit Check before recording.',
              'Record settings, meter readings, and observations. Use the graph and report tools to present evidence rather than relying on a single reading.'
            ] }
          ]}
          safety={[
            'Keep the supply off and the switch open while wiring or moving components.',
            'Use a low voltage and open the switch between readings to limit heating.',
            'Never connect an ammeter directly across a supply; it belongs in series. A voltmeter goes across the component.',
            'Do not touch a resistor or wire immediately after prolonged current flow.'
          ]}
        />
        <Box className="ohm-main-grid">
          <Box className="ohm-main-column">
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} className="ohm-control-row">
              <FormControl size="small" sx={{ minWidth: 220 }}><InputLabel id="ohm-mode-label">Experiment mode</InputLabel><Select labelId="ohm-mode-label" label="Experiment mode" value={state.mode} onChange={(event) => setState((current) => ({ ...current, mode: event.target.value as any }))}>
                <MenuItem value="verify">A — Verify Ohm’s Law</MenuItem><MenuItem value="length">B — Resistance vs length</MenuItem><MenuItem value="series-parallel">C — Series & parallel</MenuItem><MenuItem value="sandbox">D — Free play / sandbox</MenuItem>
              </Select></FormControl>
              <Paper className="ohm-slider-card" elevation={0}><Typography variant="caption">DC supply · {state.supplyVoltage.toFixed(1)} V</Typography><Slider aria-label="DC supply voltage" value={state.supplyVoltage} min={0} max={12} step={0.1} onChange={setVoltage} /></Paper>
              <Paper className="ohm-slider-card" elevation={0}><Typography variant="caption">Rheostat · {state.rheostatResistance.toFixed(1)} Ω</Typography><Slider aria-label="Rheostat resistance" value={state.rheostatResistance} min={0} max={50} step={0.5} onChange={setRheostat} /></Paper>
              <Paper className="ohm-slider-card" elevation={0}><Typography variant="caption">Test resistor · {state.fixedResistance.toFixed(1)} Ω</Typography><Slider aria-label="Test resistor value" value={state.fixedResistance} min={1} max={50} step={0.5} onChange={setFixedResistance} /></Paper>
              {state.mode === 'length' && <Paper className="ohm-slider-card" elevation={0}><Typography variant="caption">Wire length · {state.wireLengthMetres.toFixed(2)} m</Typography><Slider aria-label="Wire length" value={state.wireLengthMetres} min={0.1} max={5} step={0.05} onChange={(_, value) => setState((current) => ({ ...current, wireLengthMetres: Array.isArray(value) ? value[0] : value }))} /></Paper>}
            </Stack>
            <LabBench
              components={state.components}
              wires={state.wires}
              energized={Boolean(circuitResult)}
              supplyVoltage={state.supplyVoltage}
              ammeterRange={2}
              ammeterReading={circuitResult?.ammeter.reading ?? null}
              voltmeterReading={circuitResult?.voltmeter.reading ?? null}
              onComponentsChange={updateComponents}
              onWiresChange={(wires) => setState((current) => ({ ...current, wires }))}
              onSwitchChange={(closed) => setState((current) => ({ ...current, components: current.components.map((component) => component.id === 'switch' ? { ...component, closed } : component) }))}
            />
            <Notebook readings={state.readings} onReadingsChange={(readings) => setState((current) => ({ ...current, readings }))} onAutoRecord={autoRecord} observations={state.observations} onObservationsChange={(observations) => setState((current) => ({ ...current, observations, conclusion: observations }))} onGenerateReport={() => setReportDialog(true)} />
          </Box>
          <Box className="ohm-side-column">
            <Paper className="ohm-panel" elevation={0}>
              <Typography variant="h6" fontWeight={800}>Live instrument readings</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Source internal resistance 0.5 Ω · ammeter 0.02 Ω · voltmeter 10 MΩ</Typography>
              <Stack direction="row" spacing={2}>
                <Card className="ohm-meter-card" elevation={0}><CardContent><Typography variant="overline">Ammeter</Typography><Typography variant="h4">{circuitResult?.ammeter.reading === null ? 'OL' : circuitResult?.ammeter.reading !== undefined ? `${circuitResult.ammeter.reading.toFixed(3)} A` : '—'}</Typography><Typography variant="caption">Series instrument</Typography></CardContent></Card>
                <Card className="ohm-meter-card ohm-voltmeter-card" elevation={0}><CardContent><Typography variant="overline">Voltmeter</Typography><Typography variant="h4">{circuitResult?.voltmeter.reading === null ? 'OL' : circuitResult?.voltmeter.reading !== undefined ? `${circuitResult.voltmeter.reading.toFixed(2)} V` : '—'}</Typography><Typography variant="caption">Across the load</Typography></CardContent></Card>
              </Stack>
              {circuitResult && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>True current {circuitResult.sourceCurrent.toFixed(4)} A · true load voltage {circuitResult.resistorVoltage.toFixed(3)} V</Typography>}
            </Paper>
            <StepGuide mode={state.mode} circuitReady={Boolean(circuitResult)} readingCount={state.readings.length} onCheckCircuit={() => setMessage(circuitResult ? 'Circuit check passed: ammeter is in the series path and voltmeter is across the load.' : 'Circuit check: verify meter placement, close the key, and check all lead connections.')} />
            <GraphPlot readings={state.readings} mode={graphMode} onModeChange={setGraphMode} />
            <Paper className="ohm-panel" elevation={0}>
              <Typography variant="h6" fontWeight={800}>My lab sessions</Typography>
              {loadingSessions && <CircularProgress size={22} />}
              {!loadingSessions && sessions.length === 0 && <Typography variant="body2" color="text.secondary">Your saved Ohm’s Law sessions will appear here.</Typography>}
              <Stack spacing={1} sx={{ mt: 1 }}>
                {sessions.slice(0, 8).map((saved) => <Button key={saved._id} variant={saved._id === sessionId ? 'contained' : 'outlined'} size="small" onClick={() => resumeSession(saved)} sx={{ justifyContent: 'space-between', textTransform: 'none' }}><span>{saved.title || "Ohm's Law"} · {saved.readings?.length || 0} readings</span><span>{new Date(saved.updatedAt || saved.createdAt || Date.now()).toLocaleDateString()}</span></Button>)}
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Container>
      <Dialog open={confirmReset} onClose={() => setConfirmReset(false)}><DialogTitle>Reset this experiment?</DialogTitle><DialogContent><Typography>Your bench, local readings, and notes will be reset. Previously saved sessions remain in your account.</Typography></DialogContent><DialogActions><Button onClick={() => setConfirmReset(false)}>Cancel</Button><Button color="error" onClick={handleReset}>Reset</Button></DialogActions></Dialog>
      <Dialog open={reportDialog} onClose={() => setReportDialog(false)} maxWidth="sm" fullWidth><DialogTitle>Generate lab report</DialogTitle><DialogContent><Typography sx={{ mb: 2 }}>The report will include your instrument settings, readings, fitted resistance and R², and your written conclusion.</Typography><TextField fullWidth multiline minRows={4} label="Your conclusion" value={state.conclusion} onChange={(event) => setState((current) => ({ ...current, conclusion: event.target.value }))} /></DialogContent><DialogActions><Button onClick={() => setReportDialog(false)}>Cancel</Button><Button variant="contained" disabled={pdfBusy} onClick={() => void downloadReport()}>{pdfBusy ? 'Generating…' : 'Download PDF report'}</Button></DialogActions></Dialog>
      <Snackbar open={Boolean(message)} autoHideDuration={3200} onClose={() => setMessage('')} message={message} />
    </Box>
  );
};

export default OhmsLawLab;
